import { gql, useQuery } from '@apollo/client';

import type {
  GetWorkItemStatusCountsQuery,
  GetWorkItemStatusCountsQueryVariables,
} from './__generated__/useWorkItemStatusCounts.gql';
import type { WorkItemFiltersInput } from '../../__generated__/globalTypes';

const GET_WORK_ITEM_STATUS_COUNTS = gql`
  query GetWorkItemStatusCounts($filters: WorkItemFiltersInput) {
    getWorkItemStatusCounts(filters: $filters) {
      active
      pending
      archived
      unread
    }
  }
`;

const EMPTY_COUNTS = { active: 0, pending: 0, archived: 0, unread: 0 };

/**
 * A second write's eviction can land while the first one's re-read is still in
 * flight. Deduplicated, that re-read — issued before the second write — would
 * answer both, and the stale totals would stand until the next poll. The price
 * is one request per mounted reader instead of one shared.
 */
const UNDEDUPLICATED_CONTEXT = { queryDeduplication: false };

interface IUseWorkItemStatusCountsOptions {
  filters?: WorkItemFiltersInput;
  skip?: boolean;
  pollInterval?: number;
}

/**
 * Per-status work-item counts, plus `unread` — the unread subset of `active`,
 * which is what the notification bell's pip shows.
 *
 * Counts describe exactly the rows `getWorkItems` returns for the same
 * `filters`. Every chrome-level consumer (the bell, the Center's section
 * headers, the welcome header) should pass the same — usually absent —
 * `filters`, so they read one cache entry and a single eviction refreshes
 * them together; that sharing is what keeps the pip and the Center from
 * disagreeing.
 *
 * @param options.filters - work-item filters; must match the list query's.
 * @param options.skip - skip the query (e.g. while the gate is off).
 * @param options.pollInterval - refetch cadence in ms. Callers mounted for the
 *   life of a session (e.g. app chrome) need this; the query is otherwise only
 *   refreshed by a page load or the mutation-driven cache eviction.
 * @returns `counts` (zero-filled, never undefined; the last answer is held
 *   while an evicted read refetches), `hasCounts` — whether `counts` is an
 *   answer rather than the zero-fill, which a failed re-read keeps as the pip
 *   does — plus the Apollo query result fields.
 */
export const useWorkItemStatusCounts = ({ filters, skip, pollInterval }: IUseWorkItemStatusCountsOptions = {}) => {
  const { data, previousData, ...result } = useQuery<
    GetWorkItemStatusCountsQuery,
    GetWorkItemStatusCountsQueryVariables
  >(GET_WORK_ITEM_STATUS_COUNTS, {
    variables: { filters },
    skip,
    pollInterval,
    context: UNDEDUPLICATED_CONTEXT,
  });

  // Every work-item write evicts this field, so without `previousData` the
  // pip and the card would blink to zero on each one while the re-read lands.
  const held = skip ? undefined : (data ?? previousData);

  return { counts: held?.getWorkItemStatusCounts ?? EMPTY_COUNTS, hasCounts: held !== undefined, ...result };
};
